import ClearIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import React from 'react';
import { IMPERSONATION_SEARCH, STATUS_COLORS } from './alliance-tokens';
import { getInitials } from './bar-controls';
import { CURRENT_APP } from './current-app';

/**
 * A search result or recent agent. `raw` is the app's own record (e.g.
 * AgentSelect), kept so a recent can be impersonated with no API call.
 */
export interface SearchAgent {
  id: string;
  name: string;
  city?: string | null;
  state?: string | null;
  /** Agent status; anything other than Active gets a muted chip */
  status?: string | null;
  raw: unknown;
}

/** Thrown-error shape the search reads: an HTTP status and API message */
interface SearchError {
  response?: {
    status?: number;
    data?: { message?: string; errors?: { msg?: string | null }[] };
  };
}

const MONO = "'Roboto Mono', monospace";
const LIME = STATUS_COLORS.impersonating;

// Per-app accent (button fill) and ring (focus ring, IDs, links)
const ACCENTS: Record<
  string,
  { accent: string; fg: string; ringLight: string; ringDark: string }
> = {
  arc: {
    accent: '#2196f3',
    fg: '#ffffff',
    ringLight: '#1976d2',
    ringDark: '#4fc3f7',
  },
  contracting: {
    accent: '#00C868',
    fg: '#03182E',
    ringLight: '#00874a',
    ringDark: '#00C868',
  },
  leads: {
    accent: '#0094FF',
    fg: '#ffffff',
    ringLight: '#0070c0',
    ringDark: '#33aaff',
  },
};

const LIGHT = {
  box: '#ffffff',
  border: '#0000001f',
  text: '#1b1c1d',
  text2: '#00000099',
  text3: '#00000073',
  panel: '#ffffff',
  panelBorder: '#00000014',
  divider: '#0000000f',
  hover: '#0000000a',
  foot: '#faf8f4',
  tile: '#f1ede5',
  chip: '#0000000f',
  note: '#f6f3ec',
  err: '#c62828',
};
const DARK: typeof LIGHT = {
  box: '#3e4347',
  border: '#ffffff26',
  text: '#f3f3f3',
  text2: '#ffffffb3',
  text3: '#ffffff80',
  panel: '#2f3336',
  panelBorder: '#ffffff1f',
  divider: '#ffffff14',
  hover: '#ffffff12',
  foot: '#2a2e31',
  tile: '#ffffff14',
  chip: '#ffffff1a',
  note: '#ffffff0d',
  err: '#ff8a80',
};

const FORMATS = [
  { text: 'Jordan Ellis', mono: false },
  { text: 'Ellis, Jordan', mono: false },
  { text: 'NAA168026', mono: true },
  { text: '168026', mono: true },
];

/**
 * Normalize a typed query before it is sent: trim, collapse spaces, and
 * strip a leading NAA from an agent number so "NAA168026" and "168026"
 * search the same digits.
 *
 * The API matches the query as one substring of the stored name, which is
 * "Last, First M" ("Lewantowicz, Michael R"). So "Last, First" is sent as
 * typed (with one space after the comma), and "First Last" is reordered to
 * "Last, First" to match.
 */
const normalizeAgentQuery = (params: { query: string }) => {
  const text = params.query.trim().replace(/\s+/g, ' ');
  const number = text.match(/^naa\s*(\d+)$/i);
  if (number) {
    return number[1];
  }
  if (text.includes(',')) {
    return text.replace(/\s*,\s*/g, ', ').trim();
  }
  const words = text.split(' ');
  if (words.length > 1) {
    return `${words[words.length - 1]}, ${words.slice(0, -1).join(' ')}`;
  }
  return text;
};

/** Recents in localStorage, scoped to the signed-in user */
const readRecents = (owner: string): SearchAgent[] => {
  try {
    const stored = JSON.parse(
      localStorage.getItem(IMPERSONATION_SEARCH.recentStorageKey) || 'null',
    );
    if (stored && stored.owner === owner && Array.isArray(stored.agents)) {
      return stored.agents;
    }
  } catch (error) {
    // Unreadable or blocked storage: no recents
  }
  return [];
};
const writeRecents = (owner: string, agents: SearchAgent[]) => {
  try {
    localStorage.setItem(
      IMPERSONATION_SEARCH.recentStorageKey,
      JSON.stringify({ owner, agents }),
    );
  } catch (error) {
    // Storage full or blocked: recents just won't persist
  }
};

/** Forget the recent agents. Called when the user signs out on purpose */
export const clearImpersonationRecents = () => {
  try {
    localStorage.removeItem(IMPERSONATION_SEARCH.recentStorageKey);
  } catch (error) {
    // Blocked storage: nothing was kept
  }
};

type Phase = 'idle' | 'loading' | 'results' | 'none' | 'error';

/**
 * Impersonation search. Search runs only on Enter or the Search button,
 * never while typing; the button is off under 3 or over 50 characters and
 * while a request is in flight. Focusing the empty box lists up to 5 recent
 * agents (picked with no API call). Once an agent is picked the box turns
 * into the lime "Viewing as" card with a separate Stop button; clicking the
 * card starts a new search with Cancel.
 *
 * Who can search is decided by the API; the component only changes the
 * scope wording (all agents / your downline / {agent}'s downline).
 *
 * @param placement - sidebar (light, 268px strip) or header (ARC, dark)
 * @param scope - admin, agent or staff, for the copy only
 * @param staffAgentName - Staff: whose downline they search
 * @param owner - The signed-in username, so recents never leak between users
 * @param active - The agent being impersonated, or null
 * @param onSearch - Calls the API with the normalized query; throw on error
 *   (a 403 means the agent isn't one the user may search, e.g. outside
 *   their downline, so it shows as no results)
 * @param onPick - Start impersonating this agent
 * @param onStop - Stop impersonating
 * @param excludeIds - Never list these (the user themself, staff's agent)
 */
export function ImpersonationSearch(props: {
  placement: 'sidebar' | 'header';
  mode?: 'light' | 'dark';
  scope: 'admin' | 'agent' | 'staff';
  staffAgentName?: string | null;
  owner: string;
  active: { id: string; name: string } | null;
  onSearch: (params: { query: string }) => Promise<SearchAgent[]>;
  onPick: (agent: SearchAgent) => void;
  onStop: () => void;
  excludeIds?: string[];
}) {
  // Props
  const { active, owner } = props;
  // State
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [phase, setPhase] = React.useState<Phase>('idle');
  const [results, setResults] = React.useState<SearchAgent[]>([]);
  const [highlight, setHighlight] = React.useState(-1);
  const [switching, setSwitching] = React.useState(false);
  const [serverMessage, setServerMessage] = React.useState<string | null>(null);
  const [recents, setRecents] = React.useState<SearchAgent[]>(() =>
    readRecents(owner),
  );
  // The box the panel hangs from; state so the panel waits for it to mount
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  // Ref
  const inputRef = React.useRef<HTMLInputElement>(null);
  const requestRef = React.useRef(0);
  const lastQueryRef = React.useRef('');

  const dark =
    (props.mode || (props.placement === 'header' ? 'dark' : 'light')) ===
    'dark';
  const c = dark ? DARK : LIGHT;
  const accent = ACCENTS[CURRENT_APP.key] || ACCENTS.contracting;
  const ring = dark ? accent.ringDark : accent.ringLight;

  React.useEffect(() => {
    setRecents(readRecents(owner));
  }, [owner]);

  const trimmed = query.trim();
  const valid =
    trimmed.length >= IMPERSONATION_SEARCH.minLength &&
    trimmed.length <= IMPERSONATION_SEARCH.maxLength;
  const loading = phase === 'loading';
  const canSearch = valid && !loading;
  const showActive = Boolean(active) && !switching;
  const exclude = new Set([...(props.excludeIds || []), active?.id || '']);

  const downline =
    props.scope === 'staff'
      ? `${props.staffAgentName || 'this agent'}’s downline`
      : 'your downline';
  const scopeLabel = props.scope === 'admin' ? 'all agents' : downline;

  const reset = () => {
    requestRef.current += 1;
    setQuery('');
    setPhase('idle');
    setResults([]);
    setHighlight(-1);
    setServerMessage(null);
  };

  const close = () => {
    setOpen(false);
    if (switching) {
      setSwitching(false);
      reset();
    }
  };

  const search = async () => {
    if (!canSearch) {
      return;
    }
    const request = requestRef.current + 1;
    requestRef.current = request;
    lastQueryRef.current = trimmed;
    setPhase('loading');
    setOpen(true);
    setHighlight(-1);
    setServerMessage(null);
    try {
      const found = (
        await props.onSearch({ query: normalizeAgentQuery({ query: trimmed }) })
      ).filter((agent) => !exclude.has(agent.id));
      if (requestRef.current !== request) {
        return;
      }
      setResults(found);
      setPhase(found.length ? 'results' : 'none');
      setHighlight(found.length ? 0 : -1);
    } catch (error) {
      if (requestRef.current !== request) {
        return;
      }
      const response = (error as SearchError)?.response;
      if (response?.status === 403) {
        // The API answers 403 for an agent the user can't impersonate (not
        // in their downline), not for "can't search at all": no results
        setResults([]);
        setPhase('none');
        return;
      }
      setServerMessage(
        response?.data?.errors?.find((item) => item.msg)?.msg ||
          response?.data?.message ||
          null,
      );
      setPhase('error');
    }
  };

  const pick = (agent: SearchAgent) => {
    const next = [
      agent,
      ...recents.filter((item) => item.id !== agent.id),
    ].slice(0, IMPERSONATION_SEARCH.recentLimit);
    setRecents(next);
    writeRecents(owner, next);
    reset();
    setSwitching(false);
    setOpen(false);
    inputRef.current?.blur();
    props.onPick(agent);
  };

  const clearRecents = () => {
    setRecents([]);
    writeRecents(owner, []);
  };

  const startSwitch = () => {
    reset();
    setRecents(readRecents(owner));
    setSwitching(true);
    setOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 30);
  };

  const handleStop = () => {
    reset();
    setSwitching(false);
    setOpen(false);
    props.onStop();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (
        phase === 'results' &&
        highlight >= 0 &&
        trimmed === lastQueryRef.current
      ) {
        pick(results[highlight]);
      } else {
        search();
      }
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (phase !== 'results' || !results.length) {
        return;
      }
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setHighlight(
        (current) => (current + step + results.length) % results.length,
      );
    } else if (event.key === 'Escape') {
      close();
      inputRef.current?.blur();
    }
  };

  // Which panel body to show
  const recentList = recents.filter((agent) => !exclude.has(agent.id));
  const idle = phase === 'idle';
  const vRecent = idle && !trimmed && recentList.length > 0;
  const vRecentEmpty = idle && !trimmed && !recentList.length;
  const vShort = idle && trimmed.length > 0 && !valid;
  const vReady = idle && valid;
  const showFormats = vRecent || vRecentEmpty || vShort;

  let hint = 'Results appear after you press Enter or click Search.';
  let HintIcon = KeyboardReturnIcon;
  if (vReady) {
    hint = 'Nothing searches until you press Enter or click Search.';
  } else if (loading) {
    hint = 'Looking up agents. Search is paused until this one finishes.';
    HintIcon = HourglassEmptyIcon;
  } else if (!idle) {
    hint = 'Edit the text, then press Enter to search again.';
  }

  const nameCounts: Record<string, number> = {};
  results.forEach((agent) => {
    nameCounts[agent.name] = (nameCounts[agent.name] || 0) + 1;
  });
  const duplicateName = Object.keys(nameCounts).find(
    (name) => nameCounts[name] > 1,
  );

  const anchorWidth = anchorEl?.offsetWidth || 0;
  const panelWidth =
    props.placement === 'sidebar' ? Math.max(anchorWidth, 320) : anchorWidth;

  const agentRow = (agent: SearchAgent, index: number, isResult: boolean) => {
    const isHighlighted = isResult && index === highlight;
    const duplicate = isResult && nameCounts[agent.name] > 1;
    const location = [agent.city, agent.state].filter(Boolean).join(', ');
    const inactive =
      agent.status && agent.status.toLowerCase() !== 'active'
        ? agent.status
        : null;
    return (
      <ButtonBase
        key={agent.id}
        role="option"
        aria-selected={isHighlighted}
        onClick={() => pick(agent)}
        onMouseEnter={isResult ? () => setHighlight(index) : undefined}
        sx={{
          fontFamily: 'inherit',
          width: '100%',
          justifyContent: 'flex-start',
          textAlign: 'left',
          gap: '10px',
          p: '7px 14px',
          minHeight: 44,
          backgroundColor: isHighlighted ? c.hover : 'transparent',
          opacity: inactive ? 0.72 : 1,
          '&:hover': { backgroundColor: c.hover },
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            flex: 'none',
            borderRadius: '8px',
            backgroundColor: c.tile,
            color: c.text2,
            fontSize: 12,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {getInitials({ name: agent.name })}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                fontSize: 14,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {agent.name}
            </Box>
            {inactive ? (
              <Box
                component="span"
                sx={{
                  flex: 'none',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  p: '2px 6px',
                  borderRadius: '5px',
                  backgroundColor: c.chip,
                  color: c.text2,
                }}
              >
                {inactive}
              </Box>
            ) : null}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '6px',
              fontSize: 12,
              color: c.text2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: MONO,
                fontSize: 11.5,
                fontWeight: 700,
                color: ring,
              }}
            >
              {agent.id}
            </Box>
            {location ? (
              <Box
                component="span"
                sx={{
                  color: duplicate ? c.text : c.text2,
                  fontWeight: duplicate ? 500 : 400,
                }}
              >
                {location}
              </Box>
            ) : null}
          </Box>
        </Box>
        {isResult ? (
          <VisibilityIcon
            sx={{ fontSize: 17, color: isHighlighted ? ring : 'transparent' }}
          />
        ) : (
          <HistoryIcon sx={{ fontSize: 16, color: c.text3 }} />
        )}
      </ButtonBase>
    );
  };

  const overline = (text: string, action?: React.ReactNode) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: '12px 14px 4px',
      }}
    >
      <Box
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: c.text2,
        }}
      >
        {text}
      </Box>
      {action}
    </Box>
  );

  const stopButton = (height: number, width: number) => (
    <ButtonBase
      title="Stop impersonating"
      aria-label="Stop impersonating"
      onClick={handleStop}
      sx={{
        fontFamily: 'inherit',
        width,
        height,
        flex: 'none',
        borderRadius: '10px',
        border: `1px solid ${LIME.main}66`,
        backgroundColor: LIME.onMain,
        color: LIME.main,
        '&:hover': { backgroundColor: '#2b3d00' },
      }}
    >
      <PersonOffIcon sx={{ fontSize: 20 }} />
    </ButtonBase>
  );

  if (showActive && active) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: '6px',
          height: 48,
          letterSpacing: 'normal',
        }}
      >
        <ButtonBase
          title="Search for a different agent"
          aria-label={`Viewing as ${active.name}, ${active.id}. Search for a different agent`}
          onClick={startSwitch}
          sx={{
            fontFamily: 'inherit',
            flex: 1,
            minWidth: 0,
            justifyContent: 'flex-start',
            textAlign: 'left',
            gap: '10px',
            px: '10px',
            borderRadius: '10px',
            backgroundColor: LIME.main,
            color: LIME.onMain,
            '&:hover': { backgroundColor: '#b8ef00' },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: 28,
              height: 28,
              flex: 'none',
              borderRadius: '8px',
              backgroundColor: LIME.onMain,
              color: LIME.main,
              fontSize: 12,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {getInitials({ name: active.name })}
            <Box
              sx={{
                position: 'absolute',
                right: -5,
                bottom: -5,
                width: 16,
                height: 16,
                borderRadius: '8px',
                backgroundColor: LIME.onMain,
                color: LIME.main,
                border: `2px solid ${LIME.main}`,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VisibilityIcon sx={{ fontSize: 10 }} />
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '1px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '6px',
                whiteSpace: 'nowrap',
                opacity: 0.8,
                overflow: 'hidden',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                }}
              >
                Viewing as
              </Box>
              <Box
                component="span"
                sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 700 }}
              >
                {active.id}
              </Box>
            </Box>
            <Box
              sx={{
                fontSize: 14,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {active.name}
            </Box>
          </Box>
          <ManageSearchIcon
            sx={{ fontSize: 18, opacity: 0.8, flex: 'none', ml: '6px' }}
          />
        </ButtonBase>
        {stopButton(48, 38)}
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => open && close()}>
      <Box
        sx={{ position: 'relative', color: c.text, letterSpacing: 'normal' }}
      >
        <Box sx={{ display: 'flex', gap: '6px' }}>
          <Box
            ref={setAnchorEl}
            sx={{
              flex: 1,
              minWidth: 0,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: '0 4px 0 11px',
              boxSizing: 'border-box',
              backgroundColor: c.box,
              border: `1px solid ${open ? ring : c.border}`,
              borderRadius: '10px',
              boxShadow: open ? `0 0 0 3px ${ring}33` : 'none',
            }}
          >
            <PersonSearchIcon sx={{ fontSize: 20, color: c.text3 }} />
            <Box
              component="input"
              ref={inputRef}
              value={query}
              maxLength={IMPERSONATION_SEARCH.maxLength}
              placeholder={
                switching ? 'Search to switch agent' : 'Name or NAA number'
              }
              aria-label="Search agents"
              aria-expanded={open}
              aria-controls={open ? 'impersonation-search-panel' : undefined}
              aria-autocomplete="list"
              role="combobox"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                requestRef.current += 1;
                setQuery(
                  event.target.value.slice(0, IMPERSONATION_SEARCH.maxLength),
                );
                setPhase('idle');
                setResults([]);
                setHighlight(-1);
              }}
              onFocus={() => {
                setRecents(readRecents(owner));
                setOpen(true);
              }}
              onKeyDown={handleKeyDown}
              sx={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                fontFamily: 'Roboto, sans-serif',
                fontSize: 14,
                color: c.text,
                p: 0,
                height: '100%',
                colorScheme: dark ? 'dark' : 'light',
                '&::placeholder': { color: c.text3, opacity: 1 },
              }}
            />
            {query ? (
              <ButtonBase
                title="Clear"
                aria-label="Clear search"
                onClick={() => {
                  reset();
                  inputRef.current?.focus();
                }}
                sx={{
                  width: 28,
                  height: 28,
                  flex: 'none',
                  borderRadius: '7px',
                  color: c.text3,
                  '&:hover': { backgroundColor: c.hover },
                }}
              >
                <ClearIcon sx={{ fontSize: 16 }} />
              </ButtonBase>
            ) : null}
            <ButtonBase
              title={loading ? 'Searching…' : 'Search'}
              aria-label="Search"
              aria-disabled={!canSearch}
              aria-busy={loading}
              onClick={search}
              sx={{
                width: 32,
                height: 32,
                flex: 'none',
                borderRadius: '8px',
                boxSizing: 'border-box',
                border: `1px solid ${
                  loading || canSearch ? accent.accent : c.border
                }`,
                backgroundColor:
                  loading || canSearch ? accent.accent : 'transparent',
                color: loading || canSearch ? accent.fg : c.text3,
                opacity: loading ? 0.7 : canSearch ? 1 : 0.6,
                cursor: canSearch ? 'pointer' : 'not-allowed',
              }}
            >
              {loading ? (
                <CircularProgress
                  size={16}
                  thickness={5}
                  sx={{ color: accent.fg }}
                />
              ) : (
                <SearchIcon sx={{ fontSize: 19 }} />
              )}
            </ButtonBase>
          </Box>
          {switching ? stopButton(40, 40) : null}
        </Box>

        <Popper
          open={open && Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
          sx={{ zIndex: 1350 }}
        >
          <Box
            id="impersonation-search-panel"
            role="listbox"
            aria-label="Agents"
            sx={{
              width: panelWidth || 320,
              backgroundColor: c.panel,
              color: c.text,
              letterSpacing: 'normal',
              border: `1px solid ${c.panelBorder}`,
              borderRadius: '12px',
              boxShadow: '0 18px 40px #00000033, 0 2px 6px #0000001a',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            {switching && active ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: '8px 8px 8px 12px',
                  backgroundColor: LIME.main,
                  color: LIME.onMain,
                  fontSize: 13,
                }}
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  Viewing as <b style={{ fontWeight: 500 }}>{active.name}</b>
                </Box>
                <ButtonBase
                  onClick={close}
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: 700,
                    p: '5px 8px',
                    borderRadius: '6px',
                    '&:hover': { backgroundColor: '#1a26001f' },
                  }}
                >
                  Cancel
                </ButtonBase>
              </Box>
            ) : null}

            {vRecent ? (
              <React.Fragment>
                {overline(
                  'Recent',
                  <ButtonBase
                    onClick={clearRecents}
                    sx={{
                      fontFamily: 'inherit',
                      fontSize: 12,
                      fontWeight: 500,
                      color: ring,
                      p: '2px 4px',
                      borderRadius: '5px',
                      '&:hover': { backgroundColor: c.hover },
                    }}
                  >
                    Clear
                  </ButtonBase>,
                )}
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', pb: '6px' }}
                >
                  {recentList.map((agent, index) =>
                    agentRow(agent, index, false),
                  )}
                </Box>
              </React.Fragment>
            ) : null}

            {vRecentEmpty ? (
              <Box
                sx={{
                  p: '14px',
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: c.text2,
                }}
              >
                Agents you view as show up here, so you can switch back in one
                click.
              </Box>
            ) : null}

            {vShort ? (
              <Box
                sx={{
                  p: '14px',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: c.text2,
                }}
              >
                <KeyboardIcon sx={{ fontSize: 18, color: c.text3 }} />
                <Box sx={{ flex: 1 }}>
                  Keep typing. Searches need at least 3 characters.
                </Box>
                <Box
                  sx={{
                    fontFamily: MONO,
                    fontSize: 12,
                    fontWeight: 700,
                    color: c.text3,
                  }}
                >
                  {trimmed.length}/3
                </Box>
              </Box>
            ) : null}

            {vReady ? (
              <ButtonBase
                onClick={search}
                sx={{
                  fontFamily: 'inherit',
                  m: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  gap: '10px',
                  p: '10px 10px 10px 12px',
                  borderRadius: '10px',
                  backgroundColor: `${ring}${dark ? '1f' : '14'}`,
                  border: `1px solid ${ring}55`,
                }}
              >
                <SearchIcon sx={{ fontSize: 18, color: ring }} />
                <Box
                  sx={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.4 }}
                >
                  Search {scopeLabel} for{' '}
                  <b style={{ fontWeight: 500, wordBreak: 'break-all' }}>
                    “{trimmed}”
                  </b>
                </Box>
                <Box
                  component="span"
                  sx={{
                    flex: 'none',
                    fontFamily: MONO,
                    fontSize: 11,
                    fontWeight: 700,
                    p: '3px 6px',
                    borderRadius: '5px',
                    border: `1px solid ${c.border}`,
                    color: c.text2,
                    backgroundColor: c.box,
                  }}
                >
                  Enter ↵
                </Box>
              </ButtonBase>
            ) : null}

            {loading ? (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    p: '14px 14px 8px',
                    fontSize: 13,
                    color: c.text2,
                  }}
                >
                  <CircularProgress
                    size={16}
                    thickness={5}
                    sx={{ color: ring }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Searching {scopeLabel}…
                  </Box>
                </Box>
                {[
                  ['58%', '74%'],
                  ['44%', '66%'],
                ].map(([w1, w2]) => (
                  <Box
                    key={w1}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      p: '8px 14px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '8px',
                        backgroundColor: c.tile,
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                      }}
                    >
                      <Box
                        sx={{
                          height: 10,
                          width: w1,
                          borderRadius: '5px',
                          backgroundColor: c.tile,
                        }}
                      />
                      <Box
                        sx={{
                          height: 8,
                          width: w2,
                          borderRadius: '4px',
                          backgroundColor: c.tile,
                        }}
                      />
                    </Box>
                  </Box>
                ))}
                <Box sx={{ height: 6 }} />
              </Box>
            ) : null}

            {phase === 'results' ? (
              <React.Fragment>
                {overline(
                  props.scope === 'admin'
                    ? `${results.length} ${
                        results.length === 1 ? 'agent' : 'agents'
                      }`
                    : `${results.length} in ${downline}`,
                )}
                {duplicateName ? (
                  <Box
                    sx={{
                      m: '4px 10px',
                      display: 'flex',
                      gap: 1,
                      alignItems: 'flex-start',
                      p: '8px 10px',
                      borderRadius: '8px',
                      backgroundColor: c.note,
                      fontSize: 12,
                      lineHeight: 1.4,
                      color: c.text2,
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 15, mt: '1px' }} />
                    <Box sx={{ flex: 1 }}>
                      {nameCounts[duplicateName]} agents named {duplicateName}.
                      Check the NAA number and city before you pick.
                    </Box>
                  </Box>
                ) : null}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    pb: '6px',
                    // About 5.5 rows, then scroll
                    maxHeight: 248,
                    overflowY: 'auto',
                  }}
                >
                  {results.map((agent, index) => agentRow(agent, index, true))}
                </Box>
              </React.Fragment>
            ) : null}

            {phase === 'none' ? (
              <Box
                sx={{
                  p: '18px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  alignItems: 'flex-start',
                }}
              >
                <SearchOffIcon sx={{ fontSize: 24, color: c.text3 }} />
                <Box sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.35 }}>
                  {props.scope === 'admin'
                    ? 'No agents match.'
                    : `No agents in ${downline} match.`}
                </Box>
                <Box sx={{ fontSize: 13, lineHeight: 1.45, color: c.text2 }}>
                  “{lastQueryRef.current}” didn’t match a name or NAA number
                  {props.scope === 'admin' ? '' : ` in ${downline}`}. The search
                  worked; there just isn’t a match. Check the spelling, or try
                  the agent number.
                </Box>
              </Box>
            ) : null}

            {phase === 'error' ? (
              <Box
                role="alert"
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: c.err,
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 20 }} />
                  <Box sx={{ fontSize: 14, fontWeight: 500 }}>
                    Search failed — please try again.
                  </Box>
                </Box>
                <Box sx={{ fontSize: 13, lineHeight: 1.45, color: c.text2 }}>
                  {serverMessage ||
                    'We couldn’t reach agent search. Your text is still in the box, so retrying is one click.'}
                </Box>
                <ButtonBase
                  onClick={search}
                  sx={{
                    fontFamily: 'inherit',
                    height: 32,
                    px: '12px',
                    borderRadius: '8px',
                    backgroundColor: accent.accent,
                    color: accent.fg,
                    gap: '6px',
                    fontSize: 13,
                    fontWeight: 500,
                    '&:hover': { filter: 'brightness(1.08)' },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 16 }} />
                  Try again
                </ButtonBase>
              </Box>
            ) : null}

            {showFormats ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  p: '10px 14px',
                  borderTop: `1px solid ${c.divider}`,
                  backgroundColor: c.foot,
                  fontSize: 12,
                  lineHeight: 1.35,
                  color: c.text2,
                }}
              >
                <div>Search by name or agent number:</div>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {FORMATS.map((format) => (
                    <Box
                      key={format.text}
                      component="span"
                      sx={{
                        fontFamily: format.mono ? MONO : 'Roboto, sans-serif',
                        fontSize: 11.5,
                        fontWeight: format.mono ? 700 : 400,
                        p: '2px 6px',
                        borderRadius: '5px',
                        border: `1px solid ${c.border}`,
                        backgroundColor: c.box,
                        color: c.text,
                      }}
                    >
                      {format.text}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  p: '8px 14px',
                  borderTop: `1px solid ${c.divider}`,
                  backgroundColor: c.foot,
                  fontSize: 12,
                  lineHeight: 1.35,
                  color: c.text2,
                }}
              >
                <HintIcon sx={{ fontSize: 14 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>{hint}</Box>
                {trimmed.length >= 40 ? (
                  <Box
                    sx={{
                      fontFamily: MONO,
                      fontSize: 11,
                      fontWeight: 700,
                      color: trimmed.length >= 50 ? c.err : c.text3,
                    }}
                  >
                    {trimmed.length}/50
                  </Box>
                ) : null}
              </Box>
            )}
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
